/**
 * Lightweight Web Audio sound engine.
 * No external assets — everything is synthesized on demand.
 * Usage:
 *   import sfx from "@/lib/sfx";
 *   sfx.init();          // must be called from a user-gesture handler
 *   sfx.engine(speed);   // continuous engine rumble (speed 0..1)
 *   sfx.missile();       // one-shot whoosh
 *   sfx.monkey();        // big boom
 *   sfx.pickup();        // good chime
 *   sfx.reject();        // wrong buzz
 *   sfx.hit();           // damage thud
 *   sfx.win();           // victory jingle
 *   sfx.stop();          // cleanup
 */

class SFX {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.engineOsc = null;
    this.engineGain = null;
    this.muted = false;
  }

  init() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.4;
    this.master.connect(this.ctx.destination);
  }

  _env(gain, a = 0.005, d = 0.15, s = 0, r = 0.02, peak = 1.0) {
    const t = this.ctx.currentTime;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(peak, t + a);
    gain.gain.linearRampToValueAtTime(s, t + a + d);
    gain.gain.linearRampToValueAtTime(0, t + a + d + r);
    return t + a + d + r;
  }

  _noiseBuffer(duration = 0.3) {
    const sr = this.ctx.sampleRate;
    const buf = this.ctx.createBuffer(1, sr * duration, sr);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < ch.length; i++) ch[i] = Math.random() * 2 - 1;
    return buf;
  }

  setMuted(m) {
    this.muted = !!m;
    if (this.master) this.master.gain.value = this.muted ? 0 : 0.4;
  }

  // ---- continuous engine ----
  engine(speed = 0) {
    if (!this.ctx || this.muted) return;
    if (!this.engineOsc) {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      const lp = this.ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 800;
      osc.type = "sawtooth";
      osc.frequency.value = 60;
      gain.gain.value = 0;
      osc.connect(lp).connect(gain).connect(this.master);
      osc.start();
      this.engineOsc = osc;
      this.engineGain = gain;
    }
    const s = Math.max(0, Math.min(1, speed));
    this.engineOsc.frequency.setTargetAtTime(60 + s * 120, this.ctx.currentTime, 0.08);
    this.engineGain.gain.setTargetAtTime(0.04 + s * 0.1, this.ctx.currentTime, 0.1);
  }

  stopEngine() {
    if (!this.engineGain) return;
    this.engineGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.05);
  }

  stop() {
    this.stopEngine();
  }

  // ---- one-shots ----
  missile() {
    if (!this.ctx || this.muted) return;
    const noise = this.ctx.createBufferSource();
    noise.buffer = this._noiseBuffer(0.35);
    const bp = this.ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1400;
    bp.Q.value = 4;
    bp.frequency.exponentialRampToValueAtTime(500, this.ctx.currentTime + 0.3);
    const g = this.ctx.createGain();
    this._env(g, 0.003, 0.1, 0.05, 0.2, 0.5);
    noise.connect(bp).connect(g).connect(this.master);
    noise.start();
    noise.stop(this.ctx.currentTime + 0.4);
  }

  monkey() {
    if (!this.ctx || this.muted) return;
    // low thud + burst
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(180, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(40, this.ctx.currentTime + 0.4);
    this._env(g, 0.005, 0.2, 0.1, 0.3, 0.9);
    osc.connect(g).connect(this.master);
    osc.start();
    osc.stop(this.ctx.currentTime + 0.6);

    const noise = this.ctx.createBufferSource();
    noise.buffer = this._noiseBuffer(0.5);
    const hp = this.ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 1200;
    const g2 = this.ctx.createGain();
    this._env(g2, 0.003, 0.15, 0.05, 0.3, 0.35);
    noise.connect(hp).connect(g2).connect(this.master);
    noise.start();
    noise.stop(this.ctx.currentTime + 0.5);
  }

  pickup() {
    if (!this.ctx || this.muted) return;
    [660, 880, 1320].forEach((f, i) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = "triangle";
      o.frequency.value = f;
      g.gain.setValueAtTime(0, this.ctx.currentTime + i * 0.08);
      g.gain.linearRampToValueAtTime(0.3, this.ctx.currentTime + i * 0.08 + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + i * 0.08 + 0.22);
      o.connect(g).connect(this.master);
      o.start(this.ctx.currentTime + i * 0.08);
      o.stop(this.ctx.currentTime + i * 0.08 + 0.25);
    });
  }

  reject() {
    if (!this.ctx || this.muted) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "square";
    o.frequency.setValueAtTime(220, this.ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(110, this.ctx.currentTime + 0.25);
    this._env(g, 0.003, 0.15, 0.06, 0.2, 0.3);
    o.connect(g).connect(this.master);
    o.start();
    o.stop(this.ctx.currentTime + 0.4);
  }

  hit() {
    if (!this.ctx || this.muted) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(140, this.ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(50, this.ctx.currentTime + 0.15);
    this._env(g, 0.003, 0.1, 0, 0.15, 0.4);
    o.connect(g).connect(this.master);
    o.start();
    o.stop(this.ctx.currentTime + 0.3);
  }

  siren() {
    if (!this.ctx || this.muted) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(900, this.ctx.currentTime);
    o.frequency.linearRampToValueAtTime(600, this.ctx.currentTime + 0.25);
    o.frequency.linearRampToValueAtTime(900, this.ctx.currentTime + 0.5);
    this._env(g, 0.005, 0.3, 0.08, 0.2, 0.2);
    o.connect(g).connect(this.master);
    o.start();
    o.stop(this.ctx.currentTime + 0.55);
  }

  win() {
    if (!this.ctx || this.muted) return;
    [523, 659, 784, 1047].forEach((f, i) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = "triangle";
      o.frequency.value = f;
      const t = this.ctx.currentTime + i * 0.12;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.3, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
      o.connect(g).connect(this.master);
      o.start(t);
      o.stop(t + 0.4);
    });
  }

  // Bright two-tone power-up chime — used when the per-chassis ULTIMATE
  // arms (5 s without live cops). Stands out from win() so the player
  // associates it specifically with "your big move is ready".
  ultimateReady() {
    if (!this.ctx || this.muted) return;
    [
      { f: 660, t: 0 },
      { f: 990, t: 0.08 },
      { f: 1320, t: 0.18 },
    ].forEach(({ f, t }) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = "square";
      o.frequency.value = f;
      const startAt = this.ctx.currentTime + t;
      g.gain.setValueAtTime(0, startAt);
      g.gain.linearRampToValueAtTime(0.35, startAt + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, startAt + 0.32);
      o.connect(g).connect(this.master);
      o.start(startAt);
      o.stop(startAt + 0.35);
    });
  }
}

const sfx = new SFX();
export default sfx;
